import React from 'react';
import MetricsEditor from './MetricsEditor.jsx';
import { MAX_SETS, clampInt } from '../../utils/sport.js';

/**
 * One station of a multi-station workout: a name, how many sets, and which
 * numbers get written down for each set.
 *
 * Reps are not a field of their own — add "reps" as a value below and they get
 * logged per set like anything else.
 *
 * @param {object} station
 * @param {number} index position in the list, used for the heading
 * @param {(station: object) => void} onChange
 * @param {(() => void) | null} onRemove null when this is the last station
 */
export default function StationEditor({ station, index, onChange, onRemove }) {
  const patch = (changes) => onChange({ ...station, ...changes });

  return (
    <section className="panel-hand flex flex-col gap-3 p-3">
      <div className="flex items-center gap-2">
        <span className="shrink-0 font-display text-xs uppercase tracking-tight text-ink-soft">
          {index + 1}
        </span>

        <input
          value={station.name}
          onChange={(event) => patch({ name: event.target.value })}
          placeholder="station, e.g. bench press"
          maxLength={40}
          aria-label={`Station ${index + 1} name`}
          className="field-hand min-w-0 flex-1 font-hand text-lg leading-tight
                     placeholder:font-sans placeholder:text-xs placeholder:uppercase
                     placeholder:tracking-widest placeholder:text-ink-soft"
        />

        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            aria-label={`Remove station ${index + 1}`}
            className="shrink-0 rounded-pill px-1.5 text-xs text-accent hover:bg-accent-soft"
          >
            ✕
          </button>
        )}
      </div>

      <label className="flex items-center gap-1.5">
        <span className="text-[10px] uppercase tracking-widest text-ink-soft">sets</span>
        <input
          type="number"
          inputMode="numeric"
          min={1}
          max={MAX_SETS}
          value={station.sets}
          onChange={(event) => patch({ sets: clampInt(event.target.value, 1, MAX_SETS, 1) })}
          className="box-hand w-14 px-1 py-0.5 text-center font-hand text-lg leading-tight"
        />
      </label>

      <MetricsEditor
        metrics={station.metrics}
        onChange={(metrics) => patch({ metrics })}
        hint="e.g. kg, reps"
      />
    </section>
  );
}
